import {gql} from '@apollo/client';
import {groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {StudyDesignType, TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs, getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  FillPlateSourceTestOrdersQuery,
  FillPlateSourceTestOrdersQueryVariables,
} from 'samples/plate-maps/integrations/procedures/__generated/FillPlateSourceTestOrdersQuery';
import {
  PlateExistingTestOrdersQuery,
  PlateExistingTestOrdersQueryVariables,
} from 'samples/plate-maps/integrations/procedures/__generated/PlateExistingTestOrdersQuery';
import {
  BioentitySource,
  ContainerSource,
  MethodDefinitionVersionTestOrderConfig,
  ReagentType,
  Source,
  TestOrder,
  TestOrderInfo,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import pick from 'util/pick';

export type SourceBioentityWithTestOrders = ExcludeInaccessible<
  FillPlateSourceTestOrdersQuery['bioentities'][number]
>;
type TestOrderGQL = SourceBioentityWithTestOrders['testOrders'][number];
export type SourceContainerWithTestOrders = ExcludeInaccessible<
  FillPlateSourceTestOrdersQuery['containers'][number]
>;
export type SourceItemWithTestOrderInfos = SourceBioentityWithTestOrders | SourceContainerWithTestOrders;
type SourceContainerContentWithTestOrders = SourceContainerWithTestOrders['contents'][number];
export type SourceContainerBioentityContentWithTestOrders = ExcludeInaccessible<
  SourceContainerContentWithTestOrders['containedItem']
>;

interface TestOrdersByContainedItem {
  [containedItemId: string]: Array<TestOrder> | undefined;
}

export interface TestOrderExistingPlateContentsResult {
  contentTestOrdersByWellPosition: {[wellPosition: string]: TestOrdersByContainedItem};
  testOrderInfoById?: TestOrderInfoById;
}

export interface ProcedureTestOrderConfig extends MethodDefinitionVersionTestOrderConfig {
  procedureId: string;
  // If the test order's PMEI is non-null, it must match the method instance we're executing
  currentProcedureMethodExecutionInstanceId: string | null;
}

const ALLOWED_TEST_ORDER_STATUSES_FOR_TRANSFER = [TestOrderStatus.PENDING, TestOrderStatus.IN_PROGRESS];

/**
 * Whether trial sample information can be shown.
 *
 * This is a subset of `shouldShowTestOrderDilutionInfo`, which should generally be used instead for anything
 * surrounding showing test orders/dilutions in plate maps. This narrower guard checks only the feature flag
 * & the study design, and guards rendering errors when trial samples are selected but are not allowed.
 */
export function canShowTrialSamples(procedureStudyDesignType: StudyDesignType | null): boolean {
  return (
    BenchlingConfig.SAMPLE_TESTS_ENABLE && procedureStudyDesignType === StudyDesignType.TRIAL_SAMPLE_TESTING
  );
}

export function getProcedureTestOrderConfig({
  procedureStudyDesignType,
  procedureId,
  baseTestOrderConfig,
  procedureMethodExecutionInstanceId,
}: {
  procedureStudyDesignType: StudyDesignType | null;
  procedureId: string | null;
  baseTestOrderConfig?: MethodDefinitionVersionTestOrderConfig | null;
  procedureMethodExecutionInstanceId: string | null;
}): ProcedureTestOrderConfig | null {
  if (
    !canShowTrialSamples(procedureStudyDesignType) ||
    procedureId == null ||
    baseTestOrderConfig == null ||
    procedureMethodExecutionInstanceId == null
  ) {
    return null;
  }
  return {
    ...baseTestOrderConfig,
    procedureId,
    currentProcedureMethodExecutionInstanceId: procedureMethodExecutionInstanceId,
  };
}

export const FILL_PLATE_TEST_ORDER_FRAGMENT = gql`
  fragment FillPlateTestOrderFragment on TestOrderNode {
    id
    name
    status
    dilutionFactor
    testDefinitionCorrect {
      ... on TestDefinitionNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
    sample {
      id
    }
    procedure {
      ... on ProcedureNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
    procedureMethodExecutionInstance {
      ... on ProcedureMethodExecutionInstanceNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const FILL_PLATE_BIOENTITY_WITH_TEST_ORDERS_FRAGMENT = gql`
  fragment FillPlateBioentityWithTestOrdersFragment on RegistryItemInterface {
    id
    isTrialSample
    testOrders(statusIsOneOf: [PENDING, IN_PROGRESS]) {
      ...FillPlateTestOrderFragment
    }
    hasExpirationTracking
    tagsJson
  }
  ${FILL_PLATE_TEST_ORDER_FRAGMENT}
`;

export const FILL_PLATE_SOURCE_TEST_ORDERS_QUERY = gql`
  query FillPlateSourceTestOrdersQuery($bioentityIds: [StrictString!]!, $containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        ...FillPlateBioentityWithTestOrdersFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          id
          containedItem {
            ...FillPlateBioentityWithTestOrdersFragment
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FILL_PLATE_BIOENTITY_WITH_TEST_ORDERS_FRAGMENT}
`;

export const PLATE_EXISTING_TEST_ORDERS_QUERY = gql`
  query PlateExistingTestOrdersQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        testOrderAssociations {
          testOrder {
            ...FillPlateTestOrderFragment
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          wellPositions
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FILL_PLATE_TEST_ORDER_FRAGMENT}
`;

/**
 * Query for test order info from source container ids.
 */
async function queryTestOrdersFromSourceContainers(containerIds: Array<string>): Promise<{
  [containerId: string]: SourceContainerWithTestOrders;
}> {
  if (containerIds.length === 0) {
    return {};
  }
  const res = await doQuerySafe<FillPlateSourceTestOrdersQuery, FillPlateSourceTestOrdersQueryVariables>(
    {query: FILL_PLATE_SOURCE_TEST_ORDERS_QUERY},
    // Without some sort of network fetch policy, stale data is returned when you create multiple plate maps
    // in a row (resulting in getting the same no longer open test orders used in the first plate map).
    // This query is only triggered on user action, so network cache policy seems acceptable. cache-and-network
    // is incompatible with doQuerySafe
    {variables: {containerIds, bioentityIds: []}, fetchPolicy: 'network-only'}
  );
  return groupByUniqueKey(accessibleObjs(res.containers), (container) => container.id);
}

/**
 * Query for test order info from source entity ids.
 */
async function queryTestOrdersFromSourceEntities(bioentityIds: Array<string>): Promise<{
  [entityId: string]: SourceBioentityWithTestOrders;
}> {
  if (bioentityIds.length === 0) {
    return {};
  }
  const res = await doQuerySafe<FillPlateSourceTestOrdersQuery, FillPlateSourceTestOrdersQueryVariables>(
    {query: FILL_PLATE_SOURCE_TEST_ORDERS_QUERY},
    // Without some sort of network fetch policy, stale data is returned when you create multiple plate maps
    // in a row (resulting in getting the same no longer open test orders used in the first plate map).
    // This query is only triggered on user action, so network cache policy seems acceptable. cache-and-network
    // is incompatible with doQuerySafe
    {variables: {containerIds: [], bioentityIds}, fetchPolicy: 'network-only'}
  );
  return groupByUniqueKey(accessibleObjs(res.bioentities), (entity) => entity.id);
}

function createTestOrderMapping(testOrders: Array<TestOrderInfo>): TestOrderInfoById {
  return objectFromEntries(
    testOrders.map((testOrder) => [testOrder.id, pick(testOrder, 'id', 'name', 'dilutionFactor')])
  );
}

interface ReagentTestOrderResult<TSource extends BioentitySource | ContainerSource> {
  reagentsWithTestOrders: Array<TSource>;
  testOrderInfoById?: TestOrderInfoById;
  sourceError: string | null;
}

type BioentityReagentTestOrderResult = ReagentTestOrderResult<BioentitySource>;
type ContainerReagentTestOrderResult = ReagentTestOrderResult<ContainerSource>;

export const NULL_DILUTION_SENTINEL = 'NULL_DILUTION_SENTINEL';

/**
 * Hydrate a bioentity source by populating test orders and expanding to all (source, dilution) combos.
 *
 * This takes 1 source and returns N sources, where N is the number of distinct dilutions across the
 * reagent's test orders. Each source is identical other than the test orders. If no test orders are valid,
 * just returns all of them on one source for better error visibility.
 *
 * @param entity GQL result with test order information
 * @param baseReagent Reagent to hydrate
 * @param procedureTestOrderConfig Relevant procedure configuration (id + test defs/allowed dilution factors). If
 * null, no tests are hydrated.
 */
export function maybeHydrateBioentitySourceWithTestOrders<TBioentity extends SourceBioentityWithTestOrders>({
  entity,
  baseReagent,
  procedureTestOrderConfig,
  plateMapType,
}: {
  entity: TBioentity;
  baseReagent: BioentitySource;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): BioentityReagentTestOrderResult {
  const sourceError = validateSourceTestOrders({item: entity, procedureTestOrderConfig, plateMapType});
  // If there is no test order config, only return errors without adding test order information.
  if (procedureTestOrderConfig == null) {
    return {
      reagentsWithTestOrders: [baseReagent],
      sourceError,
    };
  }

  // We only expect to get test orders with pending or in progress statuses, but refilter just in case.
  // We also do not consider non-trial samples
  const relevantTestOrders = entity.testOrders.filter(
    (testOrder) =>
      ALLOWED_TEST_ORDER_STATUSES_FOR_TRANSFER.includes(testOrder.status) &&
      (testOrder.procedureMethodExecutionInstance == null ||
        getAccessibleOrInaccessibleId(testOrder.procedureMethodExecutionInstance) ===
          procedureTestOrderConfig.currentProcedureMethodExecutionInstanceId)
  );
  if (!relevantTestOrders.length || !entity.isTrialSample) {
    return {reagentsWithTestOrders: [baseReagent], sourceError};
  }
  // If no test orders are valid, we just return all of them
  // Otherwise, we consider only the valid ones and split by dilution
  const validTestOrders = relevantTestOrders.filter(
    (testOrder) => validateTestOrder(testOrder, procedureTestOrderConfig) == null
  );
  if (validTestOrders.length === 0) {
    return {
      reagentsWithTestOrders: [{...baseReagent, testOrders: relevantTestOrders.map((to) => pick(to, 'id'))}],
      testOrderInfoById: createTestOrderMapping(relevantTestOrders),
      sourceError,
    };
  }

  // Organize test orders by dilution
  const testOrderByDilutionFactor: {
    [dilutionFactor in number | typeof NULL_DILUTION_SENTINEL]?: Array<TestOrder>;
  } = {};
  for (const testOrder of validTestOrders) {
    const dilutionKey = testOrder.dilutionFactor ?? NULL_DILUTION_SENTINEL;
    testOrderByDilutionFactor[dilutionKey] ??= [];
    testOrderByDilutionFactor[dilutionKey].push(testOrder);
  }
  // Expand single reagent into all valid (reagent, dilution) combos
  const expandedReagents: Array<BioentitySource> = [];
  for (const testOrders of Object.values(testOrderByDilutionFactor ?? {})) {
    expandedReagents.push({...baseReagent, testOrders: testOrders?.map((to) => pick(to, 'id'))});
  }

  return {
    reagentsWithTestOrders: expandedReagents,
    testOrderInfoById: createTestOrderMapping(validTestOrders),
    sourceError,
  };
}

/**
 * Wrapper around test order hydration functions that also queries for the test order data.
 *
 * Leveraged for adding sources from search, where the test order information has not already been fetched.
 *
 * Does not query for or return test orders when procedureTestOrderConfig is null for records. For designs, we
 * do query in order to prevent adding trial samples to designs.
 */
export function maybeHydrateContainerSourceWithTestOrders({
  containerWithTestOrders,
  baseReagent,
  procedureTestOrderConfig,
  plateMapType,
}: {
  containerWithTestOrders: SourceContainerWithTestOrders;
  baseReagent: ContainerSource;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): ContainerReagentTestOrderResult {
  const sourceError = validateSourceTestOrders({
    item: containerWithTestOrders,
    procedureTestOrderConfig,
    plateMapType,
  });
  if (procedureTestOrderConfig == null) {
    return {
      reagentsWithTestOrders: [baseReagent],
      sourceError,
    };
  }

  const accessibleContainedItems = containerWithTestOrders.contents
    .map(({containedItem}) => containedItem)
    .filter(
      (containedItem): containedItem is SourceContainerBioentityContentWithTestOrders =>
        !isInaccessible(containedItem)
    );
  const accessibleContainedItemsById = objectFromEntries(
    accessibleContainedItems.map((containedItem) => [containedItem.id, containedItem])
  );

  const contentTestOrderInfoByIds: Array<TestOrderInfoById> = [];
  let contentIdWithDilutionReplicates: string | null = null;
  let hasMultipleTrialSamples: boolean = false;
  const updatedContentsByContentId: {[contentId: string]: Array<BioentitySource>} = {};
  // Iterate over contents to build a mapping from content id to hydrated content(s)
  // We keep track of which contents have dilution replicates so that we can split out the container into
  // multiple sources by dilution
  for (const content of baseReagent.contents) {
    const containedItem = accessibleContainedItemsById[content.id];
    // Missing contained item result (aka inaccessible)
    if (containedItem == null) {
      continue;
    }
    const contentResult = maybeHydrateBioentitySourceWithTestOrders({
      entity: containedItem,
      baseReagent: content,
      procedureTestOrderConfig,
      plateMapType,
    });
    if (contentResult.reagentsWithTestOrders.length > 1) {
      if (contentIdWithDilutionReplicates != null) {
        hasMultipleTrialSamples = true;
      } else {
        contentIdWithDilutionReplicates = content.id;
      }
    }
    updatedContentsByContentId[content.id] = contentResult.reagentsWithTestOrders;
    contentTestOrderInfoByIds.push(contentResult.testOrderInfoById ?? {});
  }
  const testOrderInfoById = Object.assign({}, ...contentTestOrderInfoByIds);

  const expandedReagents: Array<ContainerSource> = [];
  // Multiple trial samples is invalid & this container will be blocked; just put all the test orders on one
  if (hasMultipleTrialSamples) {
    expandedReagents.push({
      ...baseReagent,
      contents: baseReagent.contents.map((content) => ({
        ...content,
        testOrders: updatedContentsByContentId[content.id].flatMap((contentWithTestOrders) =>
          contentWithTestOrders.testOrders != null ? contentWithTestOrders.testOrders : []
        ),
      })),
    });
  } else if (contentIdWithDilutionReplicates != null) {
    // Single trial sample with multiple dilutions, expand out container reagents accordingly
    for (const dilutionContent of updatedContentsByContentId[contentIdWithDilutionReplicates]) {
      expandedReagents.push({
        ...baseReagent,
        contents: baseReagent.contents.map((content) =>
          content.id === contentIdWithDilutionReplicates
            ? dilutionContent
            : (updatedContentsByContentId[content.id]?.[0] ?? content)
        ),
      });
    }
  } else {
    // No dilution replicates, just replace all the contents
    expandedReagents.push({
      ...baseReagent,
      contents: baseReagent.contents.map((content) => updatedContentsByContentId[content.id]?.[0] ?? content),
    });
  }

  return {
    reagentsWithTestOrders: expandedReagents,
    testOrderInfoById,
    sourceError,
  };
}

/**
 * Wrapper around maybeHydrateContainerSourceWithTestOrders that also queries for the test order data.
 *
 * Leveraged for adding sources from search, where the test order information has not already been fetched.
 *
 * Does not query for or return test orders when procedureTestOrderConfig is null for records. For designs, we
 * do query in order to prevent adding trial samples to designs.
 */
export async function maybeHydrateSourceWithFetchedTestOrders<
  TSource extends BioentitySource | ContainerSource,
>(
  baseReagent: TSource,
  procedureTestOrderConfig: ProcedureTestOrderConfig | null,
  plateMapType: PlateMapType
): Promise<ReagentTestOrderResult<TSource>> {
  // For plate records outside of a procedure context, no test order hydration is necessary, so short circuit
  // before the query.
  if (procedureTestOrderConfig == null && plateMapType === PlateMapType.RECORD) {
    return {reagentsWithTestOrders: [baseReagent], sourceError: null};
  }
  if (baseReagent.type === ReagentType.SOURCE_CONTAINABLE) {
    const testOrdersByEntityId = await queryTestOrdersFromSourceEntities([baseReagent.id]);
    const entityWithTestOrders = testOrdersByEntityId[baseReagent.id];
    if (entityWithTestOrders != null) {
      return maybeHydrateBioentitySourceWithTestOrders({
        entity: entityWithTestOrders,
        baseReagent,
        procedureTestOrderConfig,
        plateMapType,
      }) as ReagentTestOrderResult<TSource>;
    }
  } else {
    const testOrdersByContainerId = await queryTestOrdersFromSourceContainers([baseReagent.id]);
    const containerWithTestOrders = testOrdersByContainerId[baseReagent.id];
    if (containerWithTestOrders != null) {
      return maybeHydrateContainerSourceWithTestOrders({
        containerWithTestOrders,
        baseReagent,
        procedureTestOrderConfig,
        plateMapType,
      }) as ReagentTestOrderResult<TSource>;
    }
  }
  // If the query doesn't return the source id, just return as is.
  return {reagentsWithTestOrders: [baseReagent], sourceError: null};
}

export const NO_ACCESS_CONTENT_SENTINEL = 'NO_ACCESS_CONTENT_SENTINEL';

/**
 * Fetch test orders associated to the plate, and return organized by well position and then sample id.
 */
export function useExistingPlateWellTestOrders({gridId, skip}: {gridId: string | null; skip: boolean}): {
  testOrderResult: TestOrderExistingPlateContentsResult;
  isPending: boolean;
} {
  const queryResult = useSkippableBenchlingQuery<
    PlateExistingTestOrdersQuery,
    PlateExistingTestOrdersQueryVariables
  >(PLATE_EXISTING_TEST_ORDERS_QUERY, {
    skip: skip || gridId == null,
    // We skip in this case anyway, but the typing expects a defined plate id
    variables: {plateIds: gridId ? [gridId] : []},
    fetchPolicy: 'cache-and-network',
  });

  if (queryResult.type !== QueryResultType.SUCCESS || accessibleObjs(queryResult.data.plates).length === 0) {
    const isPending = queryResult.type === QueryResultType.PENDING;
    return {testOrderResult: {contentTestOrdersByWellPosition: {}}, isPending};
  }
  const plate = accessibleObjs(queryResult.data.plates)[0];
  const testOrderInfoById: TestOrderInfoById = {};
  const contentTestOrdersByWellPosition: {[wellPosition: string]: TestOrdersByContainedItem} = {};

  for (const toA of plate.testOrderAssociations) {
    for (const wellPos of toA.wellPositions) {
      contentTestOrdersByWellPosition[wellPos] ??= {};
      if (isInaccessible(toA.testOrder)) {
        // Currently these aren't shown in any way. This may change with updated accessibility handling
        contentTestOrdersByWellPosition[wellPos][NO_ACCESS_CONTENT_SENTINEL] ??= [];
        contentTestOrdersByWellPosition[wellPos][NO_ACCESS_CONTENT_SENTINEL].push({
          id: getAccessibleOrInaccessibleId(toA.testOrder),
        });
      } else {
        contentTestOrdersByWellPosition[wellPos][toA.testOrder.sample.id] ??= [];
        contentTestOrdersByWellPosition[wellPos][toA.testOrder.sample.id]!.push({id: toA.testOrder.id});
        testOrderInfoById[toA.testOrder.id] = pick(toA.testOrder, 'id', 'name', 'dilutionFactor');
      }
    }
  }
  return {
    testOrderResult: {contentTestOrdersByWellPosition, testOrderInfoById},
    isPending: false,
  };
}

enum TestOrderError {
  INVALID_TEST_DEFINITION = 'INVALID_TEST_DEFINITION',
  WRONG_ASSAY_TEMPLATE = 'WRONG_ASSAY_TEMPLATE',
  INVALID_DILUTION_FACTOR = 'INVALID_DILUTION_FACTOR',
  INVALID_METHOD_EXECUTION_INSTANCE = 'INVALID_METHOD_EXECUTION_INSTANCE',
  STATUS_NOT_PENDING_OR_IN_PROGRESS = 'STATUS_NOT_PENDING_OR_IN_PROGRESS',
}

export const WRONG_ASSAY_MESSAGE = 'No test orders for this trial sample are used in this assay';
export const NO_PENDING_OR_IN_PROGRESS_TEST_ORDERS_MESSAGE =
  'Trial sample has no pending test orders or in-progress test orders for this worksheet';
export const WRONG_DILUTION_MESSAGE =
  'No test orders for this trial sample have valid dilution factors for this assay';
export const MULTIPLE_SAMPLES_MESSAGE = 'Container has multiple trial samples';
export const NO_TRIAL_SAMPLES_ON_PLATE_DESIGN_MESSAGE =
  'Trial samples cannot be added as sources to a plate design';
const TOO_MANY_TRIAL_SAMPLES_BASE_MESSAGE =
  'wells contain multiple trial samples or have multiple sources for trial samples with pending test orders';
export const TOO_MANY_TRIAL_SAMPLES_SELECTED_WELL_MESSAGE = `Selected ${TOO_MANY_TRIAL_SAMPLES_BASE_MESSAGE}`;
export const TOO_MANY_TRIAL_SAMPLES_MESSAGE = `Some ${TOO_MANY_TRIAL_SAMPLES_BASE_MESSAGE}`;
const TOO_MANY_DILUTION_FACTORS_BASE_MESSAGE = 'wells contain trial samples at multiple dilution factors';
export const TOO_MANY_DILUTION_FACTORS_MESSAGE = `Some ${TOO_MANY_DILUTION_FACTORS_BASE_MESSAGE}`;
export const TOO_MANY_DILUTION_FACTORS_SELECTED_WELL_MESSAGE = `Selected ${TOO_MANY_DILUTION_FACTORS_BASE_MESSAGE}`;

/**
 * Returns whether test order is valid to fulfill via plate maps.
 *
 * @param testOrder Test order to verify
 * @param procedureTestOrderConfig Relevant procedure configuration (id + test defs/allowed dilution factors)
 *
 * Conditions:
 * 1. Test order must be PENDING or IN_PROGRESS (prefiltered)
 * 2. Test order's test definition must be on the method definition version
 * 3. Test order is for the assay (procedure)
 * 4. Test order's dilution factor is specified in the method definition version's allowed dilution factors,
 * or if null the method must not have any dilutions specified
 * 5. If the test order has an associated method execution instance, it must match the method execution
 * instance for the worksheet.
 */
export function validateTestOrder(
  testOrder: TestOrderGQL,
  procedureTestOrderConfig: ProcedureTestOrderConfig
): TestOrderError | null {
  if (
    !procedureTestOrderConfig.testDefinitionIds.includes(
      getAccessibleOrInaccessibleId(testOrder.testDefinitionCorrect)
    )
  ) {
    return TestOrderError.INVALID_TEST_DEFINITION;
  }
  if (
    testOrder.procedure == null ||
    isInaccessible(testOrder.procedure) ||
    testOrder.procedure.id !== procedureTestOrderConfig.procedureId
  ) {
    return TestOrderError.WRONG_ASSAY_TEMPLATE;
  }
  const invalidDilutionFactor =
    testOrder.dilutionFactor != null &&
    !procedureTestOrderConfig.allowedDilutionFactors.includes(testOrder.dilutionFactor);
  const missingDilutionFactor =
    testOrder.dilutionFactor == null && procedureTestOrderConfig.allowedDilutionFactors.length > 0;
  if (invalidDilutionFactor || missingDilutionFactor) {
    return TestOrderError.INVALID_DILUTION_FACTOR;
  }
  // A null procedure method execution instance is expected for pending test orders
  // For in-progress test orders, it's expected to be non-null and match the worksheet's PMEI ID
  if (
    testOrder.procedureMethodExecutionInstance != null &&
    getAccessibleOrInaccessibleId(testOrder.procedureMethodExecutionInstance) !==
      procedureTestOrderConfig.currentProcedureMethodExecutionInstanceId
  ) {
    return TestOrderError.INVALID_METHOD_EXECUTION_INSTANCE;
  }
  // This one is special - we don't expect it to happen because all the sources should have been prefiltered
  // when fetching from the server, but we include for completeness sake to avoid a hidden dependency on that
  // behavior!
  if (!ALLOWED_TEST_ORDER_STATUSES_FOR_TRANSFER.includes(testOrder.status)) {
    return TestOrderError.STATUS_NOT_PENDING_OR_IN_PROGRESS;
  }
  return null;
}

/**
 * Check if source entity is valid w.r.t. test orders.
 *
 * See `validateTestOrder` for validity definition; entity must have at least one valid test order to be
 * valid. Non-trial samples (will not have test orders) are automatically considered valid.
 *
 * @param entity Entity with test orders to check
 * @param procedureTestOrderConfig Relevant procedure configuration (id + test defs/allowed dilution factors)
 */
function validateSourceBioentityForTestOrders<TBioentity extends SourceBioentityWithTestOrders>(
  entity: TBioentity,
  procedureTestOrderConfig: ProcedureTestOrderConfig
): string | null {
  // If it's not a trial sample then validation doesn't apply
  if (!sourceItemIsTrialSample(entity)) {
    return null;
  }

  // Otherwise must have at least one valid test order among the relevant test orders
  // We don't expect test orders that are not pending or in-progress, but stale cache data could result in
  // receiving them so silently filter out
  const openTestOrders = entity.testOrders.filter(
    (testOrder) =>
      ALLOWED_TEST_ORDER_STATUSES_FOR_TRANSFER.includes(testOrder.status) &&
      (testOrder.procedureMethodExecutionInstance == null ||
        getAccessibleOrInaccessibleId(testOrder.procedureMethodExecutionInstance) ===
          procedureTestOrderConfig.currentProcedureMethodExecutionInstanceId)
  );

  if (openTestOrders.length === 0) {
    return NO_PENDING_OR_IN_PROGRESS_TEST_ORDERS_MESSAGE;
  }

  const testOrderErrors = new Set(
    openTestOrders.map((to) => validateTestOrder(to, procedureTestOrderConfig))
  );
  // No valid test orders
  if (!testOrderErrors.has(null)) {
    if (
      testOrderErrors.has(TestOrderError.WRONG_ASSAY_TEMPLATE) ||
      testOrderErrors.has(TestOrderError.INVALID_TEST_DEFINITION)
    ) {
      return WRONG_ASSAY_MESSAGE;
    }
    if (testOrderErrors.has(TestOrderError.INVALID_DILUTION_FACTOR)) {
      return WRONG_DILUTION_MESSAGE;
    }
  }
  return null;
}

/**
 * Check if source container is valid w.r.t. test orders.
 *
 * See `validateTestOrder` for validity definition. There must be at least one valid test order. There is one
 * additional validity check, which is that the container can have at most one trial sample.
 *
 * @param container Container with test orders to check (off its contents)
 * @param procedureTestOrderConfig Relevant procedure configuration (id + test defs/allowed dilution factors)
 */
function validateSourceContainerForTestOrders(
  container: SourceContainerWithTestOrders,
  procedureTestOrderConfig: ProcedureTestOrderConfig
): string | null {
  // Empty containers are invalid for other reasons, but valid w.r.t. test orders
  if (container.contents.length === 0) {
    return null;
  }
  const trialSamples = container.contents
    .map(({containedItem}) => containedItem)
    .filter(
      (containedItem): containedItem is SourceContainerBioentityContentWithTestOrders =>
        !isInaccessible(containedItem) && containedItem.isTrialSample
    );
  // No trial samples is allowed
  if (trialSamples.length === 0) {
    return null;
  }
  // At most one trial sample is allowed
  if (trialSamples.length > 1) {
    return MULTIPLE_SAMPLES_MESSAGE;
  }
  const sample = trialSamples[0];
  return validateSourceBioentityForTestOrders(sample, procedureTestOrderConfig);
}

/**
 * Check if a source item has any trial samples.
 */
function sourceItemIsTrialSample(item: SourceItemWithTestOrderInfos): boolean {
  if (item.__typename === 'ContainerNode') {
    return item.contents.some(
      (content) => !isInaccessible(content.containedItem) && content.containedItem.isTrialSample
    );
  } else {
    return item.isTrialSample;
  }
}

/**
 * Wrapper that checks whether either source container or source entity is valid for test orders.
 */
export function validateSourceTestOrders({
  item,
  procedureTestOrderConfig,
  plateMapType,
}: {
  item: SourceItemWithTestOrderInfos;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): string | null {
  // No trial samples on designs, regardless of context.
  if (plateMapType === PlateMapType.DESIGN && sourceItemIsTrialSample(item)) {
    return NO_TRIAL_SAMPLES_ON_PLATE_DESIGN_MESSAGE;
  }
  if (procedureTestOrderConfig == null) {
    return null;
  }
  return item.__typename === 'ContainerNode'
    ? validateSourceContainerForTestOrders(item, procedureTestOrderConfig)
    : validateSourceBioentityForTestOrders(item, procedureTestOrderConfig);
}

/**
 * Merge two bioentity sources by test orders. First entity is primary; only test orders from the second are
 * used.
 */
function mergeBioentityTestOrders(
  bioentity1: BioentitySource,
  bioentity2: BioentitySource | undefined
): BioentitySource {
  if (bioentity1.id !== bioentity2?.id) {
    return bioentity1;
  }
  if (bioentity2.testOrders == null) {
    return bioentity1;
  } else if (bioentity1.testOrders == null) {
    return {...bioentity1, testOrders: bioentity2.testOrders};
  } else {
    return {...bioentity1, testOrders: [...bioentity1.testOrders, ...bioentity2.testOrders]};
  }
}

/**
 * Merge two sources by test orders.
 *
 * First source is primary; only test orders from the second get used.
 *
 * Note that this contains NO VALIDATION, and simply adds the corresponding test orders on the second source
 * directly onto the first. Expects any necessary validation to have already been done.
 *
 * @param source1 Primary source to update
 * @param source2 Source to pull additional test orders from
 * @return updated source
 */
export function mergeSourceTestOrders(source1: Source, source2: Source | undefined): Source {
  if (source1.id !== source2?.id) {
    return source1;
  }
  if (source1.type === ReagentType.SOURCE_CONTAINABLE && source2.type === ReagentType.SOURCE_CONTAINABLE) {
    return mergeBioentityTestOrders(source1, source2);
  }

  assert(
    source1.type === ReagentType.SOURCE_CONTAINER && source2.type === ReagentType.SOURCE_CONTAINER,
    'Expected sources with same id to be of same type'
  );
  const container2ContentsById = objectFromEntries(source2.contents.map((content) => [content.id, content]));
  return produce(source1, (draft) => {
    draft.contents = draft.contents.map((content1) =>
      mergeBioentityTestOrders(content1, container2ContentsById[content1.id])
    );
  });
}
